import { PROMPT_MAX_LENGTH } from "./prompts.js";
import {
  DEFAULT_VITAL_TARGET_VERSION,
  isVitalTargetVersion,
  type VitalTargetVersion,
} from "@defibrillator/vital-adapter";

export {
  DEFAULT_VITAL_TARGET_VERSION,
  VITAL_EXPORT_PROFILES,
  VITAL_TARGET_VERSIONS,
  type VitalTargetVersion,
} from "@defibrillator/vital-adapter";

/** Stable machine values for preset type controls (lowercase for prompt composition). */
export const PRESET_TYPE_VALUES = [
  "pad",
  "pluck",
  "bass",
  "lead",
  "keys",
  "arp",
  "percussion",
  "texture",
  "organ",
  "fx",
] as const;

export type PresetTypeValue = (typeof PRESET_TYPE_VALUES)[number];

export const PRESET_TYPES = [
  { value: "pad", label: "PAD" },
  { value: "pluck", label: "PLUCK" },
  { value: "bass", label: "BASS" },
  { value: "lead", label: "LEAD" },
  { value: "keys", label: "KEYS" },
  { value: "arp", label: "ARP" },
  { value: "percussion", label: "PERCUSSION" },
  { value: "texture", label: "TEXTURE" },
  { value: "organ", label: "ORGAN" },
  { value: "fx", label: "FX" },
] as const satisfies ReadonlyArray<{ value: PresetTypeValue; label: string }>;

/** Descriptive sound-design tags (not genres or artist names). */
export const PRESET_TAG_VALUES = [
  "warm",
  "dark",
  "bright",
  "analog",
  "digital",
  "vintage",
  "modern",
  "airy",
  "gritty",
  "clean",
  "wide",
  "mono",
  "evolving",
  "rhythmic",
  "cinematic",
  "ambient",
  "aggressive",
  "mellow",
] as const;

export type PresetTagValue = (typeof PRESET_TAG_VALUES)[number];

export const PRESET_TAGS = PRESET_TAG_VALUES.map((value) => ({
  value,
  label: value,
})) as ReadonlyArray<{ value: PresetTagValue; label: string }>;

/** Cap equals the fixed catalog size so allowlisted selections stay bounded. */
export const PRESET_TAG_MAX_COUNT = PRESET_TAG_VALUES.length;

export interface PresetGenerationOptions {
  type?: PresetTypeValue;
  tags: PresetTagValue[];
  /** Omitted values resolve to the current default (Vital 1.0.7). */
  vitalVersion?: VitalTargetVersion;
}

export const EMPTY_GENERATION_OPTIONS: PresetGenerationOptions = { tags: [] };

const PRESET_TYPE_SET = new Set<string>(PRESET_TYPE_VALUES);
const PRESET_TAG_SET = new Set<string>(PRESET_TAG_VALUES);
const PRESET_TYPE_LABELS = new Map<PresetTypeValue, string>(
  PRESET_TYPES.map((entry) => [entry.value, entry.label]),
);

export function isPresetTypeValue(value: unknown): value is PresetTypeValue {
  return typeof value === "string" && PRESET_TYPE_SET.has(value);
}

export function isPresetTagValue(value: unknown): value is PresetTagValue {
  return typeof value === "string" && PRESET_TAG_SET.has(value);
}

export function hasSelectedGenerationOptions(options: PresetGenerationOptions): boolean {
  return Boolean(options.type) || options.tags.length > 0;
}

export function clearGenerationOptions(): PresetGenerationOptions {
  return { tags: [] };
}

/** Keep selected tags in catalog order for stable prompts and UI. */
export function orderPresetTags(tags: readonly string[]): PresetTagValue[] {
  const selected = new Set(tags.filter(isPresetTagValue));
  return PRESET_TAG_VALUES.filter((tag) => selected.has(tag));
}

/**
 * Validate and normalize option payloads from UI or untrusted request bodies.
 * Unknown values are dropped; tags are deduped, ordered, and capped.
 */
export function sanitizeGenerationOptions(
  input: { type?: unknown; tags?: unknown; vitalVersion?: unknown } | null | undefined,
): PresetGenerationOptions {
  const type = isPresetTypeValue(input?.type) ? input.type : undefined;
  const rawTags = Array.isArray(input?.tags) ? input.tags : [];
  const tags = orderPresetTags(rawTags).slice(0, PRESET_TAG_MAX_COUNT);
  const vitalVersion = isVitalTargetVersion(input?.vitalVersion) ? input.vitalVersion : undefined;
  return {
    ...(type ? { type } : {}),
    tags,
    ...(vitalVersion ? { vitalVersion } : {}),
  };
}

export function togglePresetType(
  current: PresetGenerationOptions,
  type: PresetTypeValue,
): PresetGenerationOptions {
  const nextType = current.type === type ? undefined : type;
  return {
    ...(nextType ? { type: nextType } : {}),
    tags: current.tags,
    ...(current.vitalVersion ? { vitalVersion: current.vitalVersion } : {}),
  };
}

export function togglePresetTag(
  current: PresetGenerationOptions,
  tag: PresetTagValue,
): PresetGenerationOptions {
  const selected = new Set(current.tags);
  if (selected.has(tag)) {
    selected.delete(tag);
  } else {
    selected.add(tag);
  }
  const tags = orderPresetTags([...selected]);
  return {
    ...(current.type ? { type: current.type } : {}),
    tags,
    ...(current.vitalVersion ? { vitalVersion: current.vitalVersion } : {}),
  };
}

export function setVitalTargetVersion(
  current: PresetGenerationOptions,
  vitalVersion: VitalTargetVersion,
): PresetGenerationOptions {
  return {
    ...(current.type ? { type: current.type } : {}),
    tags: current.tags,
    vitalVersion,
  };
}

export function resolvedVitalTargetVersion(options: PresetGenerationOptions): VitalTargetVersion {
  return options.vitalVersion ?? DEFAULT_VITAL_TARGET_VERSION;
}

export function formatTagList(tags: readonly string[]): string {
  if (tags.length === 0) return "";
  if (tags.length === 1) return tags[0]!;
  if (tags.length === 2) return `${tags[0]} and ${tags[1]}`;
  return `${tags.slice(0, -1).join(", ")}, and ${tags[tags.length - 1]}`;
}

export function formatGenerationOptions(options: PresetGenerationOptions): string {
  const sanitized = sanitizeGenerationOptions(options);
  const parts: string[] = [];
  if (sanitized.type) {
    parts.push(PRESET_TYPE_LABELS.get(sanitized.type) ?? sanitized.type.toUpperCase());
  }
  if (sanitized.tags.length > 0) {
    parts.push(sanitized.tags.join(", "));
  }
  if (sanitized.vitalVersion && sanitized.vitalVersion !== DEFAULT_VITAL_TARGET_VERSION) {
    parts.push(`Vital ${sanitized.vitalVersion}`);
  }
  return parts.join(" · ");
}

function indefiniteArticle(type: PresetTypeValue): "a" | "an" {
  // "FX" is pronounced "eff-ex".
  if (type === "fx") return "an";
  return /^[aeiou]/i.test(type) ? "an" : "a";
}

function buildOptionsPrefix(options: PresetGenerationOptions): string {
  const parts: string[] = [];
  if (options.type) {
    const label = options.type.toUpperCase();
    parts.push(`Create ${indefiniteArticle(options.type)} ${label} sound.`);
  }
  if (options.tags.length > 0) {
    parts.push(`Make it ${formatTagList(options.tags)}.`);
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

/**
 * Fold optional type/tags into a typed create prompt.
 * When no options are selected, returns the trimmed user prompt unchanged.
 * If the result would exceed PROMPT_MAX_LENGTH, truncates only the user-direction tail.
 */
export function composeGenerationPrompt(
  prompt: string,
  options: PresetGenerationOptions,
): string {
  const sanitized = sanitizeGenerationOptions(options);
  const userPrompt = prompt.replace(/\s+/g, " ").trim();
  if (!hasSelectedGenerationOptions(sanitized)) {
    return userPrompt;
  }

  const prefix = buildOptionsPrefix(sanitized);
  if (!userPrompt) {
    return prefix.slice(0, PROMPT_MAX_LENGTH);
  }

  const directionLabel = "User direction: ";
  const separator = " ";
  const fixed = `${prefix}${separator}${directionLabel}`;
  const maxUserLength = PROMPT_MAX_LENGTH - fixed.length;
  if (maxUserLength <= 0) {
    return prefix.slice(0, PROMPT_MAX_LENGTH);
  }

  const truncatedUser =
    userPrompt.length > maxUserLength ? userPrompt.slice(0, maxUserLength).trimEnd() : userPrompt;
  return `${fixed}${truncatedUser}`;
}

/** Map UI preset types onto random-provider musical facet roles when possible. */
export const PRESET_TYPE_TO_FACET_ROLE = {
  pad: "pad",
  pluck: "pluck",
  bass: "bass",
  lead: "lead",
  keys: "keys",
  /** ARP UX role → sequence-capable facet (not ordinary lead). */
  arp: "sequence",
  percussion: "percussion",
  texture: "texture",
  organ: "keys",
  fx: "effect",
} as const satisfies Record<
  PresetTypeValue,
  "bass" | "lead" | "pad" | "pluck" | "keys" | "percussion" | "texture" | "effect" | "sequence"
>;

export function generationOptionsForRequest(
  options: PresetGenerationOptions,
): { type?: PresetTypeValue; tags?: PresetTagValue[] } | undefined {
  const sanitized = sanitizeGenerationOptions(options);
  if (!hasSelectedGenerationOptions(sanitized) && !sanitized.vitalVersion) return undefined;
  return {
    ...(sanitized.type ? { type: sanitized.type } : {}),
    ...(sanitized.tags.length > 0 ? { tags: sanitized.tags } : {}),
    ...(sanitized.vitalVersion ? { vitalVersion: sanitized.vitalVersion } : {}),
  };
}
